import { AiConversation } from "../../../models/AiAssistant";

/**
 * How long a conversation stays "current" after its last activity. Opening
 * the assistant later than this starts a fresh thread; the old one is one
 * click away in the empty state and in the history dropdown. Short on
 * purpose: a resumed thread carries its whole history into every new turn,
 * so an old one mostly adds unrelated context (and tokens) to a new question.
 */
export const AI_CONVERSATION_RESUME_WINDOW_MS = 15 * 60 * 1000;

/**
 * Whether a conversation should be picked up again without the user asking
 * for it. Unfinished work always wins: a turn still being produced or a
 * proposal waiting for approval must not disappear from view just because
 * the user was away.
 */
export function isResumable(conversation: AiConversation, now: number): boolean {
	if (conversation.status === "running") return true;
	if (conversation.pendingApprovals.length > 0) return true;
	return now - new Date(conversation.updated).getTime() < AI_CONVERSATION_RESUME_WINDOW_MS;
}

/**
 * The conversation the sidebar should show when the user has not chosen one
 * themselves: the newest one if it is still current, otherwise nothing,
 * meaning "start fresh". Only the newest conversation is considered; a
 * pending approval further down the list was left behind deliberately when
 * the user started another thread.
 */
export function resolveActiveConversation(
	conversations: AiConversation[],
	now: number = Date.now()
): AiConversation | undefined {
	const newest = conversations[0];
	if (!newest) return undefined;
	return isResumable(newest, now) ? newest : undefined;
}
